import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, Sparkles, Calendar, Wallet, Globe } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Checkbox,
  NotificationCard,
  Input,
  RadioGroup,
  RadioItem,
  MarketingCard,
  useToast,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatZARFromCents } from '@/shared/utils/format';
import { domainService } from '@/domains/domains-registry/services/domainService';
import {
  RegistrantForm,
  EMPTY_REGISTRANT,
  trimRegistrant,
  validateRegistrant,
  type RegistrantInput,
} from '../domains/components/RegistrantForm';

/**
 * StudioStarterOrderPage — Phase 10 Studio Starter funnel.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_BRD.md §5.4
 *       _bmad-output/PHASE_10_SERVICES_ARCHITECTURE.md §2.1
 *
 * 4-step wizard rendered as one screen for simplicity:
 *   1. Pick a payment plan (full / 3 / 6 / 12-month installment)
 *   2. If installment plan, choose deposit (R500–R3,990)
 *   3. Confirm details + accept T&Cs
 *   4. Pay deposit (or full amount) via PayFast
 *
 * On submit: calls service-order-create with sku_code='studio-starter',
 * plan_months (or undefined for full upfront), deposit_amount_cents.
 * Redirects to /pay/:invoice_id?token=… for PayFast checkout.
 *
 * 2026-09-30 (onboarding integrity S1-5 / gap G4): the customer now chooses
 * the bundled domain here — register a free .co.za, use an existing domain,
 * or decide later. Until then this page sent nothing, the EF wrote 'later',
 * and a self-serve Studio order never got its free domain or hosting. The
 * server builds the bundle with the same code an agent's quotation uses.
 */

const STUDIO_TOTAL_CENTS = 399000;
const DEPOSIT_FLOOR_CENTS = 50000; // R500 — flexible deposit (locked 2026-07-16; was R1,000). Keep in sync with service-order-create.
type PlanChoice = 'full' | '3' | '6' | '12';
type DomainMode = 'register' | 'existing' | 'later';
type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'error';

const DOMAIN_OPTIONS: Array<{ value: DomainMode; label: string; blurb: string }> = [
  { value: 'register', label: 'Register a free .co.za', blurb: 'Included for the first year. Registered as soon as your payment clears.' },
  { value: 'existing', label: 'I already have a domain', blurb: 'We host your site on the domain you own. You point it at us when the site goes live.' },
  { value: 'later', label: 'Decide at the consultation', blurb: 'We will help you choose one when we meet.' },
];

interface PlanOption {
  value: PlanChoice;
  label: string;
  blurb: string;
}

const PLAN_OPTIONS: PlanOption[] = [
  { value: 'full', label: 'Pay R3,990 in full', blurb: 'No installments. Get the discount on cash flow you keep.' },
  { value: '3', label: '3-month plan', blurb: 'Deposit from R500 + 3 monthly installments.' },
  { value: '6', label: '6-month plan', blurb: 'Deposit from R500 + 6 monthly installments.' },
  { value: '12', label: '12-month plan', blurb: 'Deposit from R500 + 12 monthly installments.' },
];

export default function StudioStarterOrderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [planChoice, setPlanChoice] = useState<PlanChoice>('full');
  const [depositCents, setDepositCents] = useState(DEPOSIT_FLOOR_CENTS);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [domainMode, setDomainMode] = useState<DomainMode>('register');
  const [registerFqdn, setRegisterFqdn] = useState('');
  const [existingFqdn, setExistingFqdn] = useState('');
  const [availability, setAvailability] = useState<Availability>('idle');
  const [registrant, setRegistrant] = useState<RegistrantInput>({
    ...EMPTY_REGISTRANT,
    email: user?.email ?? '',
  });

  // Debounced live availability check for the free .co.za.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const fqdn = registerFqdn.trim().toLowerCase();
    if (domainMode !== 'register' || !fqdn.endsWith('.co.za') || fqdn.length <= '.co.za'.length) {
      setAvailability('idle');
      return;
    }
    setAvailability('checking');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await domainService.checkAvailability(fqdn);
        setAvailability(res.available ? 'available' : 'taken');
      } catch {
        setAvailability('error');
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [domainMode, registerFqdn]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planMonths = planChoice === 'full' ? null : Number(planChoice);
  const upfrontCents = planMonths === null ? STUDIO_TOTAL_CENTS : depositCents;
  const balanceCents = planMonths === null ? 0 : STUDIO_TOTAL_CENTS - depositCents;
  const installmentCents = planMonths === null ? 0 : Math.floor(balanceCents / planMonths);
  // Step numbers shift when the deposit step is shown.
  const domainStep = planMonths === null ? 2 : 3;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!termsAccepted) {
      setError('Please accept the Studio Starter terms and conditions to continue.');
      return;
    }

    if (planMonths !== null && depositCents < DEPOSIT_FLOOR_CENTS) {
      setError(`Minimum deposit is ${formatZARFromCents(DEPOSIT_FLOOR_CENTS)}.`);
      return;
    }
    if (planMonths !== null && depositCents >= STUDIO_TOTAL_CENTS) {
      setError(`Deposit cannot equal or exceed the full price ${formatZARFromCents(STUDIO_TOTAL_CENTS)}. Choose "Pay in full" instead.`);
      return;
    }

    if (domainMode === 'register') {
      const fqdn = registerFqdn.trim().toLowerCase();
      if (!fqdn.endsWith('.co.za') || fqdn.length <= '.co.za'.length) {
        setError('Enter the .co.za you would like, e.g. mybusiness.co.za. Studio includes a free .co.za only.');
        return;
      }
      if (availability === 'taken') {
        setError(`${fqdn} is already taken. Try another name.`);
        return;
      }
      const regErr = validateRegistrant(registrant, { requireAddress: false });
      if (regErr) {
        setError(`Domain owner details: ${regErr}`);
        return;
      }
    }
    if (domainMode === 'existing' && !existingFqdn.trim().includes('.')) {
      setError('Enter the domain you already own, e.g. mybusiness.co.za.');
      return;
    }

    if (!user) {
      navigate(`/sign-up?return=${encodeURIComponent('/app/order/services/studio-starter')}`);
      return;
    }

    setSubmitting(true);
    try {
      const idempotencyKey = crypto.randomUUID();

      const body: Record<string, unknown> = {
        sku_code: 'studio-starter',
        terms_accepted: true,
      };
      if (planMonths !== null) {
        body.plan_months = planMonths;
        body.deposit_amount_cents = depositCents;
      }
      body.fqdn_mode = domainMode;
      if (domainMode === 'register') {
        body.register_fqdn = registerFqdn.trim().toLowerCase();
        body.register_registrant = trimRegistrant(registrant);
      } else if (domainMode === 'existing') {
        body.existing_fqdn = existingFqdn.trim().toLowerCase();
      }

      const { data, error: efErr } = await supabase.functions.invoke<{
        ok: true;
        service_order_id: string;
        invoice_id: string;
        invoice_number: string;
        payment_token: string;
        total_cents: number;
      }>('service-order-create', {
        body,
        headers: { 'idempotency-key': idempotencyKey },
      });

      if (efErr) {
        let msg = efErr.message;
        const ctx = (efErr as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = await ctx.clone().json();
            if (parsed?.code === 'email_not_verified') {
              msg = 'Please verify your email address first. Check your inbox for the verification link.';
            } else if (parsed?.message) {
              msg = parsed.message;
            }
          } catch { /* ignore */ }
        }
        setError(msg);
        return;
      }

      if (!data?.ok) {
        setError('Order could not be created. Please try again or contact support.');
        return;
      }

      const payUrl = `/pay/${data.invoice_id}?token=${data.payment_token}`;
      toast.toast({
        tone: 'success',
        title: 'Order ready',
        body: `Invoice ${data.invoice_number} created. Redirecting to PayFast…`,
      });
      window.location.href = payUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="Order SightHost Studio Starter"
      breadcrumbs={[
        { label: 'Services', href: '/app/services' },
        { label: 'Studio Starter' },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Order summary — shared marketing MarketingCard, rendered lighter in
            the portal's light theme (same design language as the /services
            marketing page it was reached from). */}
        <MarketingCard
          variant="featured"
          icon={Sparkles}
          eyebrow="Bespoke website"
          title="SightHost Studio Starter — R3,990"
          description="Bespoke website + free .co.za domain (1 yr) + 3 months free SightHost Basic hosting. Delivered in 48 working hours from the moment we have your complete brief. Optional add-ons: custom logo (R300), company profile (R850)."
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Step 1: Plan choice */}
          <Card>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-display text-lg font-semibold text-foreground">1. Pick a payment plan</h3>
              </div>
              <RadioGroup value={planChoice} onValueChange={(v) => setPlanChoice(v as PlanChoice)}>
                <div className="space-y-2">
                  {PLAN_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      htmlFor={`plan-${opt.value}`}
                      className={`flex items-start gap-3 p-4 rounded-soft border cursor-pointer transition ${
                        planChoice === opt.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <RadioItem value={opt.value} id={`plan-${opt.value}`} className="mt-1" />
                      <div className="flex-1">
                        <p className="text-foreground font-medium">{opt.label}</p>
                        <p className="text-muted-foreground text-sm mt-1">{opt.blurb}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            </div>
          </Card>

          {/* Step 2: Deposit slider (installment plans only) */}
          {planMonths !== null && (
            <Card>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-lg font-semibold text-foreground">2. Choose your deposit</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">
                  Minimum {formatZARFromCents(DEPOSIT_FLOOR_CENTS)}. The bigger your deposit, the smaller your monthly installments.
                </p>
                <Input
                  type="number"
                  min={DEPOSIT_FLOOR_CENTS / 100}
                  max={(STUDIO_TOTAL_CENTS - 100) / 100}
                  step="100"
                  value={depositCents / 100}
                  onChange={(e) => setDepositCents(Math.round(Number(e.target.value) * 100))}
                  className="text-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-muted rounded-soft p-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Pay today</p>
                    <p className="text-xl font-bold text-foreground mt-1">{formatZARFromCents(depositCents)}</p>
                  </div>
                  <div className="bg-muted rounded-soft p-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Then × {planMonths}</p>
                    <p className="text-xl font-bold text-foreground mt-1">
                      {formatZARFromCents(installmentCents)}<span className="text-sm text-muted-foreground">/mo</span>
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Step: the bundled domain (S1-5) */}
          <Card>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                <h3 className="font-display text-lg font-semibold text-foreground">{domainStep}. Your domain</h3>
              </div>
              <RadioGroup value={domainMode} onValueChange={(v) => setDomainMode(v as DomainMode)}>
                <div className="space-y-2">
                  {DOMAIN_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      htmlFor={`domain-${opt.value}`}
                      className={`flex items-start gap-3 p-4 rounded-soft border cursor-pointer transition ${
                        domainMode === opt.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <RadioItem value={opt.value} id={`domain-${opt.value}`} className="mt-1" />
                      <div className="flex-1">
                        <p className="text-foreground font-medium">{opt.label}</p>
                        <p className="text-muted-foreground text-sm mt-1">{opt.blurb}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </RadioGroup>

              {domainMode === 'register' && (
                <div className="space-y-4">
                  <div>
                    <Input
                      placeholder="mybusiness.co.za"
                      value={registerFqdn}
                      onChange={(e) => setRegisterFqdn(e.target.value)}
                      aria-label="The .co.za you would like"
                    />
                    <p className="mt-2 text-sm" aria-live="polite">
                      {availability === 'checking' && <span className="text-muted-foreground">Checking availability…</span>}
                      {availability === 'available' && <span className="text-success">Available. It is yours once your payment clears.</span>}
                      {availability === 'taken' && <span className="text-danger">Already taken. Try another name.</span>}
                      {availability === 'error' && <span className="text-muted-foreground">Could not check right now. We will check again when you order.</span>}
                    </p>
                  </div>
                  <div>
                    <p className="mb-3 text-sm text-muted-foreground">
                      Domain owner details, required by the registry. The address is optional.
                    </p>
                    <RegistrantForm value={registrant} onChange={setRegistrant} idPrefix="studio-reg" requireAddress={false} />
                  </div>
                </div>
              )}

              {domainMode === 'existing' && (
                <Input
                  placeholder="mybusiness.co.za"
                  value={existingFqdn}
                  onChange={(e) => setExistingFqdn(e.target.value)}
                  aria-label="The domain you already own"
                />
              )}
            </div>
          </Card>

          {/* Confirm + accept T&Cs */}
          <Card>
            <div className="p-6 space-y-4">
              <h3 className="font-display text-lg font-semibold text-foreground">{domainStep + 1}. Confirm + accept terms</h3>

              {user ? (
                <div className="bg-muted rounded-soft p-4 border border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Billing email</p>
                  <p className="text-foreground font-medium">{user.email}</p>
                </div>
              ) : (
                <NotificationCard
                  tone="info"
                  title="Sign in to continue"
                  body="You'll be prompted to sign in (or create an account) when you submit. Your selections will be remembered."
                />
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(v === true)}
                  aria-label="Accept Studio Starter terms"
                />
                <span className="text-sm text-foreground">
                  I've read and accept the{' '}
                  <a
                    href="/terms#studio-starter"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-ink hover:underline"
                  >
                    Studio Starter terms
                  </a>
                  , including the 48-working-hour SLA definition, the non-refundable clause once work has commenced, and the 3-month free hosting cliff at month 4.
                </span>
              </label>

              {error && (
                <NotificationCard tone="danger" title="Could not place order" body={error} />
              )}
            </div>
          </Card>

          <div className="flex items-center justify-between">
            <Button asChild variant="ghost">
              <Link to="/services/website-design">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Link>
            </Button>
            <Button type="submit" size="lg" variant="marketing" loading={submitting} disabled={submitting || !termsAccepted}>
              {submitting ? 'Creating order…' : `Pay ${formatZARFromCents(upfrontCents)} via PayFast`}
              {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          Payment processed securely by PayFast. Receipt emailed to your billing address.{' '}
          {planMonths !== null && (
            <>After your deposit clears, we'll set up the {planMonths}-month installment plan in a separate confirmation email.</>
          )}
        </p>
      </div>
    </UniversalPage>
  );
}
